import { getImageUrlString } from '@src/utils/dRepUtils';

/**
 * Image URL guards for crawlable surfaces.
 *
 * A DRep's `image` comes straight from on-chain metadata and is whatever the
 * operator put there. On mainnet 12 of the registered DReps use inline
 * `data:image/...;base64` values, the largest 262 KB, 1.13 MB across all of
 * them. Those are fine as an <img> src in an authenticated profile view, but
 * on the pages built for crawlers they are actively harmful:
 *
 *   - As `og:image` / `twitter:image` a data URI is rejected by every social
 *     scraper and by Google, and because it is non-empty it *displaces* the
 *     banner fallback — so the card breaks instead of degrading.
 *   - Inlined into server HTML they inflate the page. `/dreps` renders 60
 *     cards, so one page can carry a large share of that 1.13 MB, on exactly
 *     the route that exists to score well on Core Web Vitals.
 */

/**
 * A URL safe to advertise to an external consumer (metadata, JSON-LD).
 * Returns null for data URIs, relative paths and anything unparseable.
 */
export function shareableImageUrl(value?: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
      ? trimmed
      : null;
  } catch {
    return null;
  }
}

/**
 * A URL safe to put in an `<img src>` on a server-rendered page: same rule,
 * but site-relative paths are kept since they cost nothing and resolve.
 */
export function renderableImageUrl(value?: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed;
  return shareableImageUrl(trimmed);
}

/**
 * Data-URI image types the `/drep-avatar/<id>` route will serve.
 *
 * `image/svg+xml` is deliberately absent: an SVG served from our own origin
 * can carry script, so honouring an operator-supplied one would be stored XSS
 * on drep.id. One mainnet DRep publishes an SVG avatar today, and it falls
 * back to the banner / default avatar rather than being served.
 *
 * Shared with the route rather than duplicated. When these two disagreed,
 * `avatarUrl` handed out a URL the route then 404'd — which is worse than no
 * avatar at all, because `og:image` pointing at a 404 breaks the whole card
 * instead of falling back to the banner.
 */
export const ROUTABLE_AVATAR_MIME = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/avif',
]);

/** `data:image/png;base64,…` → `image/png`; null for anything else. */
export function dataUriMime(value?: string | null): string | null {
  const match = /^data:(image\/[a-z0-9.+-]+);base64,/i.exec(
    value?.trim() || ''
  );
  return match ? match[1].toLowerCase() : null;
}

/**
 * The avatar URL to publish for a DRep, in `og:image`, JSON-LD `image` and
 * `<img src>` alike.
 *
 * Three cases:
 *   - An `http(s)` URL is used as-is; it is already a fetchable resource.
 *   - A `data:` URI is replaced by `/drep-avatar/<drepId>`, which decodes and
 *     serves the same bytes as a real HTTP resource. See that route for why it
 *     exists and why it is neither disk-cached nor served from
 *     `/social/serveImage`.
 *   - Anything else (a relative path, junk, an unparseable value) yields null
 *     and the caller falls back to the site banner or the default avatar.
 *
 * `drepId` is the CIP-129 identifier, which is also what the route resolves
 * against, so the two cannot drift.
 */
export function avatarUrl(record: {
  image?: string | null;
  drepId: string;
}): string | null {
  const direct = shareableImageUrl(record.image);
  if (direct) return direct;

  // Only for a type the route will actually serve — otherwise this would
  // advertise a URL that 404s.
  const mime = dataUriMime(record.image);
  if (mime && ROUTABLE_AVATAR_MIME.has(mime)) {
    return `/drep-avatar/${encodeURIComponent(record.drepId)}`;
  }

  return null;
}

/**
 * Swap a base64 data URI out of already-fetched profile metadata for the
 * `/drep-avatar/<id>` URL, before that metadata reaches the client component
 * that renders it.
 *
 * The `avatarUrl` path above covers metadata, JSON-LD and the directory cards,
 * which all read the Elasticsearch record. The on-page avatar does not: it
 * renders `dRepDetails.metadata.body.image`, resolved from the DRep's anchor
 * URL. For the ten DReps whose anchor resolves *and* whose image is a data
 * URI, that blob reached the markup untouched — React emits it twice (markup
 * plus flight payload), which is how one profile shipped 1.37 MB of HTML.
 *
 * Substituted only when the record also holds a data URI, i.e. when the route
 * has the same bytes to serve. The record is the indexer's copy of the same
 * anchor document, so the two agree except for indexer lag — the same lag the
 * rest of the SEO surface already carries.
 *
 * The anchor's `image` is usually not a bare string — CIP-119 documents wrap
 * it as `{"@type":"ImageObject","contentUrl":…}`, or nest that under `@value`.
 * `getImageUrlString` already knows all three shapes, so it does the reading;
 * writing a plain string back is safe because it accepts that shape too.
 */
export function withRoutedAvatar<
  T extends { metadata?: { body?: { image?: unknown } } | null },
>(details: T, record?: { image?: string | null; drepId: string } | null): T {
  const anchorImage = getImageUrlString(details.metadata?.body?.image);
  if (!anchorImage || !dataUriMime(anchorImage)) return details;

  const routed = record && avatarUrl(record);
  if (!routed || !routed.startsWith('/drep-avatar/')) return details;

  return {
    ...details,
    metadata: {
      ...details.metadata,
      body: { ...details.metadata?.body, image: routed },
    },
  };
}
